
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  collection,
  getDocs,
  query,
  orderBy,
  where,
  limit as qLimit,
  doc,
  getDoc,
  deleteDoc,
  Timestamp,
} from "firebase/firestore";
import { getAuth, onAuthStateChanged, signOut } from "firebase/auth";
import { db } from "../firebase";
import { useCuerpo } from "../context/CuerpoContext";

/** ---------------------------
 * Utils
 * -------------------------- */
function fmtDate(tsOrDate) {
  try {
    const d =
      tsOrDate?.toDate?.() ||
      (tsOrDate instanceof Date ? tsOrDate : tsOrDate ? new Date(tsOrDate) : null);
    if (!d) return "—";
    return d.toLocaleString("es-CL", { dateStyle: "short", timeStyle: "short" });
  } catch {
    return "—";
  }
}

function ymdFromAny(v) {
  try {
    if (!v) return "";
    if (typeof v === "string") {
      if (/^\d{4}-\d{2}-\d{2}$/.test(v)) return v;
      const d = new Date(v);
      if (!isNaN(d.getTime())) return d.toISOString().slice(0, 10);
      return "";
    }
    const d = v?.toDate?.() || (v instanceof Date ? v : new Date(v));
    if (isNaN(d.getTime())) return "";
    return d.toISOString().slice(0, 10);
  } catch {
    return "";
  }
}

function monthFromYmd(ymd) {
  if (!ymd || typeof ymd !== "string") return 0;
  const m = Number(ymd.slice(5, 7));
  return Number.isFinite(m) ? m : 0;
}

function normalizeText(s) {
  return String(s || "").trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
}


// ================================
// Asistentes por alcance (cuerpo/compañía)
// ================================
function getAsistentesArray(p) {
  const v =
    // formatos más comunes
    p?.asistentes ??
    p?.asistentes_detalle ??
    p?.asistentesDetalle ??
    p?.asistentes_personal ??
    p?.personal_asistente ??
    // ✅ formato real guardado en tus partes
    p?.asistentesSnapshot ??
    p?.asistentes_snapshot ??
    [];

  if (Array.isArray(v)) return v.filter(Boolean);
  if (v && typeof v === "object") return Object.values(v).filter(Boolean);
  return [];
}

function matchCompania(a, b) {
  const A = normalizeText(a);
  const B = normalizeText(b);
  if (!A || !B) return false;
  return A === B || A.includes(B) || B.includes(A);
}

function countAsistentesForScope(p, scope) {
  const arr = getAsistentesArray(p);

  // Si no hay arreglo, caemos a los campos antiguos (solo para cuerpo)
  if (!arr.length) {
    const n1 = Array.isArray(p?.asistentesIds) ? p.asistentesIds.length : 0;
    const n2 = typeof p?.asistentesCount === "number" ? p.asistentesCount : 0;
    // ⚠️ Si es informe por compañía y no hay detalle, NO podemos desglosar => 0
    if (scope?.mode === "compania") return 0;
    return Math.max(n1, n2, 0);
  }

  // Cuerpo
  if (!scope || scope.mode !== "compania" || !scope.nombre) return arr.length;

  // Compañía
  return arr.filter((a) => matchCompania(a?.compania, scope.nombre)).length;
}


function uniqById(list) {
  const seen = new Set();
  const out = [];
  for (const it of list || []) {
    const id = it?.id || it?.docId || it?.parteId || it?.correlativo;
    if (!id) continue;
    if (seen.has(id)) continue;
    seen.add(id);
    out.push(it);
  }
  return out;
}

function getCompaniaNumberFromNombre(nombre) {
  const n = normalizeText(nombre);
  if (n.includes("primera")) return 1;
  if (n.includes("segunda")) return 2;
  if (n.includes("tercera")) return 3;
  if (n.includes("cuarta")) return 4;
  if (n.includes("quinta")) return 5;

  // Fallback por dígitos (ej: "3ª", "3°", "3")
  const m = String(nombre || "").match(/(\d+)/);
  if (m) {
    const k = Number(m[1]);
    if (k >= 1 && k <= 20) return k;
  }
  return null;
}

function logoPathForScope(scope) {
  // scope = { mode: "cuerpo" } or { mode: "compania", nombre: "..." }
  if (scope?.mode === "compania") {
    const n = getCompaniaNumberFromNombre(scope?.nombre);
    if (n) return `/logos/logo-compania${n}.png`;
    return `/logos/logo-compania1.png`;
  }
  return `/logos/logo-cuerpo.png`;
}

function headerTitle(scope, cuerpoNombre) {
  if (scope?.mode === "compania") return `Informe generado por Digital Evolution para ${scope?.nombre || "Compañía"}`;
  return `Informe generado por Digital Evolution para ${cuerpoNombre || "Cuerpo de Bomberos"}`;
}

function openPrintWindow({ title, html }) {
  // ✅ Sin popups: imprimimos desde un iframe oculto (Safari/Chrome no lo bloquean)
  try {
    const iframe = document.createElement("iframe");
    iframe.style.position = "fixed";
    iframe.style.right = "0";
    iframe.style.bottom = "0";
    iframe.style.width = "0";
    iframe.style.height = "0";
    iframe.style.border = "0";
    iframe.style.opacity = "0";
    iframe.setAttribute("aria-hidden", "true");
    // srcdoc soportado en navegadores modernos
    iframe.srcdoc = html;
    document.body.appendChild(iframe);

    const cleanup = () => {
      try { document.body.removeChild(iframe); } catch {}
    };

    iframe.onload = () => {
      try {
        // algunos navegadores requieren pequeño delay
        setTimeout(() => {
          try {
            iframe.contentWindow?.focus();
            iframe.contentWindow?.print();
          } finally {
            // limpiamos después de un rato para no cortar el print
            setTimeout(cleanup, 1500);
          }
        }, 50);
      } catch {
        cleanup();
        alert("No se pudo generar el PDF.");
      }
    };
  } catch (e) {
    console.warn("print iframe error", e);
    alert("No se pudo generar el PDF.");
  }
}

/** ---------------------------
 * Perfil
 * -------------------------- */
async function cargarPerfil(uid) {
  const ref = doc(db, "usuarios_partes", uid);
  const snap = await getDoc(ref);
  if (!snap.exists()) return null;
  return { id: snap.id, ...snap.data() };
}

function canDelete(perfil) {
  const rol = normalizeText(perfil?.rol || "");
  // ✅ Admin/superadmin + Ayudante General pueden eliminar (tolerante a texto extra)
  return rol === "admin"
    || rol === "superadmin"
    || rol.includes("ayudante general");
}





function canEdit(perfil) {
  const rol = normalizeText(perfil?.rol || "");
  // ✅ Admin/superadmin + Ayudantes + Central de Alarmas pueden editar (tolerante a texto extra)
  return rol === "admin"
    || rol === "superadmin"
    || rol.includes("ayudante de compania")  // "Ayudante de Compañía"
    || rol.includes("ayudante de cuerpo")    // "Ayudante de Cuerpo"
    || rol.includes("ayudante general")      // "Ayudante General"
    || rol.includes("central de alarmas");   // "Central de Alarmas"
}





function buildSearchIndex(p) {
  const parts = [
    p?.correlativo,
    p?.comuna,
    p?.direccion,
    p?.jurisdiccion,
    p?.tipoEmergenciaNombre || p?.tipo_emergencia_nombre,
    p?.subTipoEmergenciaNombre || p?.sub_tipo_emergencia_nombre,
    (p?.companias_despachadas_nombres || []).join(" "),
  ];
  return normalizeText(parts.filter(Boolean).join(" | "));
}

function toCSV(rows) {
  const esc = (v) => {
    const s = String(v ?? "");
    const needs = /[",\n]/.test(s);
    const out = s.replaceAll('"', '""');
    return needs ? `"${out}"` : out;
  };

  const header = [
    "Correlativo",
    "Fecha",
    "Hora",
    "Tipo",
    "Subtipo",
    "Dirección",
    "Jurisdicción",
    "Comuna",
    "Compañías despachadas",
    "Asistentes",
  ];

  const lines = [header.map(esc).join(",")];

  for (const p of rows) {
    const ymd = p?.fechaEmergencia || ymdFromAny(p?.createdAt);
    const hora = p?.horaDespacho || "";
    const tipo = p?.tipoEmergenciaNombre || p?.tipo_emergencia_nombre || "";
    const sub = p?.subTipoEmergenciaNombre || p?.sub_tipo_emergencia_nombre || "";
    const comps = (p?.companias_despachadas_nombres || []).join(" / ");
    const asistentes = String(p?.asistentesCount ?? (p?.asistentesIds || []).length ?? "");
    lines.push(
      [
        p?.correlativo || "",
        ymd || "",
        hora,
        tipo,
        sub,
        p?.direccion || "",
        p?.jurisdiccion || "",
        p?.comuna || "",
        comps,
        asistentes,
      ].map(esc).join(",")
    );
  }

  // BOM para Excel
  return "\ufeff" + lines.join("\n");
}

/** ---------------------------
 * UI
 * -------------------------- */
export default function PartesLista() {
  const navigate = useNavigate();
  const { cuerpoSlug } = useCuerpo();
  const partesBase = `/${cuerpoSlug}/partes`;
  const auth = getAuth();

  const [user, setUser] = useState(null);
  const [perfil, setPerfil] = useState(null);

  const [cuerpoNombre, setCuerpoNombre] = useState("");

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [anio, setAnio] = useState(new Date().getFullYear());

  const [partes, setPartes] = useState([]);
  const [enCreacion, setEnCreacion] = useState([]);

  // catálogo compañías (para selector de informe/gráfico)
  const [companiasCat, setCompaniasCat] = useState([]); // [{id,nombre,cuerpo_id}]
  const [scopeMode, setScopeMode] = useState("cuerpo"); // cuerpo | compania
  const [scopeCompaniaId, setScopeCompaniaId] = useState("");

  // filtros
  const [mes, setMes] = useState("all");
  const [tipo, setTipo] = useState("all");
  const [fechaExacta, setFechaExacta] = useState("");
  const [q, setQ] = useState("");

  /** Auth */
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (u) => {
      setUser(u || null);
      if (!u) {
        setPerfil(null);
        setLoading(false);
        return;
      }
      setLoading(true);
      setLoadError(null);
      const p = await cargarPerfil(u.uid);
      setPerfil(p);
      setLoading(false);
    });
    return () => unsub();
  }, [auth]);

  /** Cargar catálogo de compañías y nombre del cuerpo */
  useEffect(() => {
    let mounted = true;

    async function run() {
      const cid =
        perfil?.cuerpo_id ||
        perfil?.cuerpoId ||
        perfil?.cuerpo ||
        perfil?.cuerpo_doc_id ||
        "";

      try {
        if (!cid) {
          if (mounted) setCompaniasCat([]);
        } else {
          const snapC = await getDocs(
            query(collection(db, "companias"), where("cuerpo_id", "==", cid))
          );
          const list = snapC.docs
            .map((d) => ({ id: d.id, ...d.data() }))
            .filter((x) => x?.nombre)
            .sort((a, b) => String(a?.nombre || "").localeCompare(String(b?.nombre || "")));
          if (mounted) setCompaniasCat(list);
        }
      } catch (e) {
        console.warn("⚠️ No se pudo cargar companias del Cuerpo:", e);
        if (mounted) setCompaniasCat([]);
      }

      // Cuerpo nombre desde doc cuerpos_bomberos/{id}
      try {
        if (cid) {
          const snap = await getDoc(doc(db, "cuerpos_bomberos", cid));
          const nombre = snap.exists() ? snap.data()?.nombre : "";
          if (mounted) setCuerpoNombre(nombre || "");
        } else if (perfil?.cuerpo_nombre) {
          if (mounted) setCuerpoNombre(perfil.cuerpo_nombre);
        } else {
          if (mounted) setCuerpoNombre("");
        }
      } catch (e) {
        console.warn("⚠️ No se pudo cargar cuerpo:", e);
        if (mounted) setCuerpoNombre("");
      }
    }

    run();
    return () => {
      mounted = false;
    };
  }, [perfil]);

  /** Cargar partes por año */
  useEffect(() => {
    // Esperar a que termine de cargarse el perfil antes de consultar por Cuerpo.
    // onAuthStateChanged entrega primero el usuario y luego cargarPerfil() resuelve
    // usuarios_partes; sin esta espera se generaba un falso error "sin cuerpo_id".
    if (!user || !perfil) return;
    let mounted = true;

    async function run() {
      setLoading(true);
      setLoadError(null);
      const cid =
        perfil?.cuerpo_id ||
        perfil?.cuerpoId ||
        perfil?.cuerpo ||
        perfil?.cuerpo_doc_id ||
        "";

      try {
        if (!cid) throw new Error("El perfil no tiene cuerpo_id asignado.");

        // Una sola condición Firestore evita índices compuestos durante la migración.
        // Año y orden se resuelven en cliente, pero NUNCA se descargan partes de otro Cuerpo.
        const qPartes = query(
          collection(db, "partes_emergencia"),
          where("cuerpo_id", "==", cid),
          qLimit(2500)
        );

        const snap = await getDocs(qPartes);
        const list = snap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .filter((p) => {
            const y = Number(p?.correlativo_year) ||
              Number(String(p?.correlativo || "").slice(-4)) ||
              Number(ymdFromAny(p?.fechaEmergencia).slice(0, 4));
            return y === Number(anio);
          })
          .sort((a, b) => Number(b?.correlativo_num || 0) - Number(a?.correlativo_num || 0));

        if (mounted) {
          setPartes(uniqById(list));
          setLoadError(null);
        }
      } catch (e) {
        console.error("❌ No se pudieron cargar partes del Cuerpo:", e);
        setLoadError({ stage: "query_cuerpo", code: e?.code || null, message: String(e?.message || e) });
        if (mounted) setPartes([]);
      }

      // Partes en creación (anti-duplicidad), aislados por Cuerpo
      try {
        if (!cid) throw new Error("El perfil no tiene cuerpo_id asignado.");
        const nowMs = Timestamp.now().toMillis();
        const qEC = query(
          collection(db, "partes_en_creacion"),
          where("cuerpo_id", "==", cid),
          qLimit(500)
        );
        const snapEC = await getDocs(qEC);
        const listEC = snapEC.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .filter((x) => Number(x?.anio) === Number(anio))
          .filter((x) => (x?.expires_at?.toMillis?.() || 0) > nowMs)
          .sort((a, b) => (b?.expires_at?.toMillis?.() || 0) - (a?.expires_at?.toMillis?.() || 0));
        if (mounted) setEnCreacion(uniqById(listEC));
      } catch (e) {
        console.warn("⚠️ No se pudieron cargar partes_en_creacion del Cuerpo:", e);
        if (mounted) setEnCreacion([]);
      }

      setLoading(false);
    }

    run();
    return () => {
      mounted = false;
    };
  }, [user, anio, perfil]);

  const tiposDisponibles = useMemo(() => {
    const set = new Set();
    for (const p of partes) {
      const t = p?.tipoEmergenciaNombre || p?.tipo_emergencia_nombre;
      if (t) set.add(t);
    }
    return Array.from(set).sort((a, b) => String(a).localeCompare(String(b)));
  }, [partes]);

  const partesFiltrados = useMemo(() => {
    const qn = normalizeText(q);
    return (partes || []).filter((p) => {
      const ymd = p?.fechaEmergencia || ymdFromAny(p?.createdAt);
      const m = monthFromYmd(ymd);

      if (mes !== "all" && Number(mes) !== m) return false;

      const t = p?.tipoEmergenciaNombre || p?.tipo_emergencia_nombre || "";
      if (tipo !== "all" && tipo !== t) return false;

      if (fechaExacta) {
        const ymd2 = ymdFromAny(ymd);
        if (ymd2 !== fechaExacta) return false;
      }

      if (qn) {
        const idx = p.__idx || buildSearchIndex(p);
        return idx.includes(qn);
      }

      return true;
    });
  }, [partes, mes, tipo, fechaExacta, q]);

  // guardar índice de búsqueda (solo en memoria)
  useEffect(() => {
    setPartes((prev) => prev.map((p) => (p.__idx ? p : { ...p, __idx: buildSearchIndex(p) })));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [partes.length]);

  const scope = useMemo(() => {
    if (scopeMode === "compania") {
      const c = companiasCat.find((x) => x.id === scopeCompaniaId);
      return { mode: "compania", id: c?.id || "", nombre: c?.nombre || "" };
    }
    return { mode: "cuerpo" };
  }, [scopeMode, scopeCompaniaId, companiasCat]);

  function limpiarFiltros() {
    setMes("all");
    setTipo("all");
    setFechaExacta("");
    setQ("");
  }

  async function handleSalir() {
    await signOut(auth);
    navigate("/login");
  }

  async function eliminarParte(parteId) {
    if (!canDelete(perfil)) return;
    if (!window.confirm("¿Eliminar este parte? Esta acción no se puede deshacer.")) return;
    try {
      await deleteDoc(doc(db, "partes_emergencia", parteId));
      setPartes((prev) => prev.filter((p) => p.id !== parteId));
    } catch (e) {
      console.error("❌ Error eliminando:", e);
      alert("No se pudo eliminar.");
    }
  }

  function generarPDFParte(parte) {
  const scopeForLogo = (() => {
    const compNombre = parte?.compania_nombre || parte?.compania || null;
    return scopeMode === "compania"
      ? { mode: "compania", nombre: scope?.nombre || compNombre || "" }
      : { mode: "cuerpo" };
  })();

  const logo = logoPathForScope(scopeForLogo);
  const titulo = headerTitle(scopeForLogo, cuerpoNombre);

  const ymd = parte?.fechaEmergencia || ymdFromAny(parte?.createdAt);
  const hora = parte?.horaDespacho || parte?.hora_despacho || "—";
  const tipoTxt = parte?.tipoEmergenciaNombre || parte?.tipo_emergencia_nombre || "—";
  const subTxt =
    parte?.subTipoEmergenciaNombre ||
    parte?.sub_tipo_emergencia_nombre ||
    parte?.subTipoEmergenciaId ||
    parte?.sub_tipo_emergencia_id ||
    "—";

  const zonaRaw =
    parte?.zona ??
    parte?.unidadesParte?.zona ??
    parte?.unidades_parte?.zona ??
    parte?.unidadesParteZona ??
    parte?.tipoZona ??
    parte?.zonaTipo ??
    "";

  const zonaNice = (() => {
    const z = String(zonaRaw || "").trim().toLowerCase();
    if (!z) return "";
    if (z === "urbano") return "Urbano";
    if (z === "rural") return "Rural";
    if (z === "costa") return "Costa";
    return z.charAt(0).toUpperCase() + z.slice(1);
  })();

  const operadoraObj = parte?.operadora || null;
  const operadoraTxt =
    (typeof operadoraObj === "string" ? operadoraObj : null) ||
    operadoraObj?.nombre ||
    operadoraObj?.nombreCompleto ||
    operadoraObj?.usuario ||
    operadoraObj?.rut ||
    operadoraObj?.id ||
    "—";

  const esc = (v) =>
    String(v ?? "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");

  const fmtTS = (v) => {
    try {
      if (!v) return "—";
      if (typeof v === "object" && v.seconds != null) {
        const d = new Date(v.seconds * 1000);
        return d.toLocaleString("es-CL");
      }
      if (v instanceof Date) return v.toLocaleString("es-CL");
      if (typeof v === "number") return new Date(v).toLocaleString("es-CL");
      const d = new Date(v);
      if (!isNaN(d.getTime())) return d.toLocaleString("es-CL");
    } catch (e) {}
    return "—";
  };

  const prettyLabel = (key) => {
    const map = {
      tipo: "Tipo",
      vehiculos: "Vehículos",
      extricacion: "Extricación",
      personas: "Personas",
      lesionados: "Lesionados",
      fallecidos: "Fallecidos",
      conductor: "Conductor",
      acompanante: "Acompañante",
      patente: "Patente",
      marca: "Marca",
      modelo: "Modelo",
      tipoVehiculo: "Tipo de vehículo",
      tipoCombustible: "Tipo de combustible",
      propietarioNombre: "Nombre propietario",
      propietarioRut: "RUT propietario",
      propietarioTelefono: "Teléfono propietario",
      lugar: "Lugar",
      observacion: "Observación",
      observaciones: "Observaciones",
      cantidad: "Cantidad",
      detalle: "Detalle",
      descripcion: "Descripción",
      comuna: "Comuna",
      direccion: "Dirección",
      unidad: "Unidad",
      unidades: "Unidades",
      obacCompaniaNombre: "OBAC Compañía",
      obacCuerpoNombre: "OBAC Cuerpo",
      conductorNombre: "Conductor",
      horaSalida: "Hora salida",
      horaLlegada: "Hora llegada",
      kmSalida: "KM salida",
      kmLlegada: "KM llegada"
    };
    return map[key] || key.replace(/([A-Z])/g, " $1").replace(/^./, s => s.toUpperCase());
  };

  const renderValue = (value) => {
    if (value === null || value === undefined || value === "") return "—";
    if (typeof value === "boolean") return value ? "Sí" : "No";
    return esc(String(value));
  };

  const renderDetailData = (data) => {
    if (!data || typeof data !== "object") {
      return `<div class="muted">—</div>`;
    }

    const bannedKeys = ["flow", "flujo", "wizard", "step", "_flow", "_wizard", "__flow"];

    const rows = Object.entries(data)
      .filter(([k]) => !bannedKeys.includes(String(k).toLowerCase()))
      .map(([key, value]) => {
        if (Array.isArray(value)) {
          if (!value.length) {
            return `
              <div class="subbox">
                <div class="lbl">${esc(prettyLabel(key))}</div>
                <div class="p">—</div>
              </div>
            `;
          }

          const arrayItems = value.map((item, idx) => {
            if (item && typeof item === "object") {
              const inner = Object.entries(item)
                .filter(([k]) => !bannedKeys.includes(String(k).toLowerCase()))
                .map(([k, v]) => `
                  <div class="kvrow">
                    <div class="k">${esc(prettyLabel(k))}</div>
                    <div class="v">${renderValue(v)}</div>
                  </div>
                `)
                .join("");

              return `
                <div class="nested">
                  <div class="nested-title">${esc(prettyLabel(key))} ${idx + 1}</div>
                  <div class="kv">${inner || `<div class="muted">—</div>`}</div>
                </div>
              `;
            }

            return `
              <div class="subbox">
                <div class="lbl">${esc(prettyLabel(key))} ${idx + 1}</div>
                <div class="p">${renderValue(item)}</div>
              </div>
            `;
          }).join("");

          return `<div class="stack">${arrayItems}</div>`;
        }

        if (value && typeof value === "object") {
          const inner = Object.entries(value)
            .filter(([k]) => !bannedKeys.includes(String(k).toLowerCase()))
            .map(([k, v]) => `
              <div class="kvrow">
                <div class="k">${esc(prettyLabel(k))}</div>
                <div class="v">${renderValue(v)}</div>
              </div>
            `)
            .join("");

          return `
            <div class="nested">
              <div class="nested-title">${esc(prettyLabel(key))}</div>
              <div class="kv">${inner || `<div class="muted">—</div>`}</div>
            </div>
          `;
        }

        return `
          <div class="kvrow">
            <div class="k">${esc(prettyLabel(key))}</div>
            <div class="v">${renderValue(value)}</div>
          </div>
        `;
      })
      .join("");

    return rows || `<div class="muted">—</div>`;
  };

  const realiza = parte?.realizaParte || parte?.realiza_parte || null;
  const unidadesArr = parte?.unidadesParte || parte?.unidades_parte || [];
  const asistentesArr = parte?.asistentesSnapshot || parte?.asistentes_snapshot || [];
  const detalleEspecifico = parte?.detalleEspecifico || parte?.detalle_especifico || null;
  const obacCuerpoRoot = parte?.obacCuerpo || parte?.obac_cuerpo || null;

  const companiasNombres = (
    parte?.companias_despachadas_nombres ||
    parte?.companiasDespachadasNombres ||
    []
  ).filter(Boolean);

  const realizaHtml = realiza
    ? `<div class="kv">
        ${Object.entries(realiza).map(([k, v]) => `
          <div class="kvrow">
            <div class="k">${esc(prettyLabel(k))}</div>
            <div class="v">${renderValue(v)}</div>
          </div>
        `).join("")}
      </div>`
    : `<div class="muted">—</div>`;

  const obacCuerpoHtml = obacCuerpoRoot
    ? `<div class="kv">
        <div class="kvrow"><div class="k">Nombre</div><div class="v">${renderValue(obacCuerpoRoot?.nombre)}</div></div>
        <div class="kvrow"><div class="k">RUT</div><div class="v">${renderValue(obacCuerpoRoot?.rut || obacCuerpoRoot?.id)}</div></div>
        <div class="kvrow"><div class="k">Cargo</div><div class="v">${renderValue(obacCuerpoRoot?.cargo)}</div></div>
        <div class="kvrow"><div class="k">Compañía</div><div class="v">${renderValue(obacCuerpoRoot?.compania)}</div></div>
      </div>`
    : `<div class="muted">—</div>`;

  const unidadesHtml = Array.isArray(unidadesArr) && unidadesArr.length
    ? `<table class="tbl">
        <thead>
          <tr>
            <th>Unidad</th>
            <th>H. Salida</th>
            <th>H. Llegada</th>
            <th>KM Salida</th>
            <th>KM Llegada</th>
            <th>OBAC Compañía</th>
            <th>Conductor</th>
            <th>OBS</th>
          </tr>
        </thead>
        <tbody>
          ${unidadesArr.map(u => {
            const unidad = u?.unidadCodigo || u?.unidad || u?.clave || u?.codigo || u?.nombre || "—";
            const hSalida = u?.horaSalida || u?.hora_salida || "—";
            const hLlegada = u?.horaLlegada || u?.hora_llegada || "—";
            const kmSalida = u?.kmSalida ?? u?.km_salida ?? "—";
            const kmLlegada = u?.kmLlegada ?? u?.km_llegada ?? "—";

            const obacCompania =
              u?.obacCompania?.nombre ||
              u?.obacCompaniaNombre ||
              u?.obacCompania?.rut ||
              u?.obacCompania?.id ||
              u?.obac ||
              u?.obacCompaniaId ||
              "—";

            const c = u?.conductor ?? u?.maquinista ?? u?.conductorNombre ?? null;
            const conductor =
              typeof c === "string"
                ? c
                : (c && typeof c === "object"
                    ? (c.nombre || c.usuario || c.rut || c.id || "—")
                    : "—");

            const obs = u?.obs || u?.observaciones || u?.observacion || "";

            return `<tr>
              <td>${esc(unidad)}</td>
              <td>${esc(hSalida)}</td>
              <td>${esc(hLlegada)}</td>
              <td>${esc(kmSalida)}</td>
              <td>${esc(kmLlegada)}</td>
              <td>${esc(obacCompania)}</td>
              <td>${esc(conductor)}</td>
              <td>${esc(obs || "—")}</td>
            </tr>`;
          }).join("")}
        </tbody>
      </table>`
    : `<div class="muted">— Sin unidades —</div>`;

  const asistentesHtml = Array.isArray(asistentesArr) && asistentesArr.length
    ? `<table class="tbl">
        <thead>
          <tr>
            <th>Nombre</th>
            <th>RUT</th>
            <th>Cargo</th>
            <th>Compañía</th>
          </tr>
        </thead>
        <tbody>
          ${asistentesArr.map(a => {
            const nombre = a?.nombre || a?.nombreCompleto || "—";
            const rut = a?.rut || "—";
            const cargo = a?.cargo || a?.rol || "—";
            const cia = a?.compania || a?.companiaNombre || a?.compania_nombre || "—";
            return `<tr>
              <td>${esc(nombre)}</td>
              <td>${esc(rut)}</td>
              <td>${esc(cargo)}</td>
              <td>${esc(cia)}</td>
            </tr>`;
          }).join("")}
        </tbody>
      </table>`
    : `<div class="muted">—</div>`;

  const detalleHtml = detalleEspecifico
    ? `<div class="box">
        <div class="lbl">Tipo detalle</div>
        <div class="val">${esc(detalleEspecifico?.tipo || "—")}</div>
        <div style="height:10px"></div>
        <div class="lbl">Datos</div>
        <div style="height:8px"></div>
        ${renderDetailData(detalleEspecifico?.data)}
      </div>`
    : `<div class="muted">—</div>`;

  const creadoTxt = fmtTS(parte?.createdAt || parte?.created_at);
  const editadoTxt = fmtTS(parte?.updatedAt || parte?.updated_at);
  const editadoPor = (() => {
    const u = parte?.updatedBy || parte?.updated_by || null;
    if (!u) return "—";
    if (typeof u === "string") return u;
    return u.usuario || u.nombre || u.email || u.rut || u.uid || "—";
  })();

  const html = `<!doctype html>
<html>
<head>
<meta charset="utf-8" />
<title>${esc(parte?.correlativo || "Parte")}</title>
<style>
  body{font-family:ui-sans-serif,system-ui,-apple-system,Segoe UI,Roboto,Arial;padding:28px;color:#0b1220;}
  .head{display:flex;gap:16px;align-items:center;border-bottom:2px solid #e5e7eb;padding-bottom:14px;margin-bottom:18px;}
  .logo{width:68px;height:68px;object-fit:contain;}
  .t1{font-weight:900;font-size:18px;margin:0;}
  .t2{margin:4px 0 0;color:#334155;font-size:12px;}
  .meta{display:grid;grid-template-columns:1fr 1fr;gap:10px;margin-top:14px;}
  .box{border:1px solid #e5e7eb;border-radius:12px;padding:12px;break-inside:avoid-page;}
  .subbox{border:1px solid #e5e7eb;border-radius:10px;padding:10px;background:#fff;break-inside:avoid-page;}
  .lbl{font-size:11px;color:#64748b;font-weight:800;letter-spacing:.02em;}
  .val{margin-top:4px;font-size:14px;font-weight:800;}
  .section{margin-top:14px;break-inside:avoid-page;}
  .section h3{margin:0 0 8px;font-size:13px;letter-spacing:.02em;text-transform:uppercase;}
  .p{white-space:pre-wrap;line-height:1.35;}
  .muted{color:#64748b;}
  .tbl{width:100%;border-collapse:collapse;font-size:12px;}
  .tbl th,.tbl td{border:1px solid #e5e7eb;padding:8px;vertical-align:top;}
  .tbl th{background:#f8fafc;text-align:left;}
  .kv{border:1px solid #e5e7eb;border-radius:12px;overflow:hidden;break-inside:avoid-page;}
  .kvrow{display:grid;grid-template-columns:220px 1fr;}
  .kvrow .k{padding:8px 10px;font-weight:800;color:#334155;background:#f8fafc;border-bottom:1px solid #e5e7eb;}
  .kvrow .v{padding:8px 10px;border-bottom:1px solid #e5e7eb;}
  .kvrow:last-child .k,.kvrow:last-child .v{border-bottom:none;}
  .stack{display:grid;gap:10px;}
  .nested{margin-top:8px;break-inside:avoid-page;}
  .nested-title{font-size:12px;font-weight:900;color:#0f172a;margin:0 0 6px;}
  @page{size:auto;margin:14mm;}
</style>
</head>
<body>
  <div class="head">
    <img class="logo" src="${logo}" />
    <div>
      <p class="t1">${esc(titulo)}</p>
      <p class="t2">Correlativo: <b>${esc(parte?.correlativo || "—")}</b></p>
    </div>
  </div>

  <div class="meta">
    <div class="box"><div class="lbl">Creado</div><div class="val">${esc(creadoTxt)}</div></div>
    <div class="box"><div class="lbl">Última edición</div><div class="val">${esc(editadoTxt)}</div></div>
    <div class="box"><div class="lbl">Editado por</div><div class="val">${esc(editadoPor)}</div></div>
    <div class="box"><div class="lbl">Fecha emergencia</div><div class="val">${esc(ymd || "—")}</div></div>
    <div class="box"><div class="lbl">Hora despacho</div><div class="val">${esc(hora)}</div></div>
    <div class="box"><div class="lbl">Tipo</div><div class="val">${esc(tipoTxt)}</div></div>
    <div class="box"><div class="lbl">Subtipo</div><div class="val">${esc(subTxt)}</div></div>
    <div class="box" style="grid-column:1/-1"><div class="lbl">Dirección</div><div class="val">${esc(parte?.direccion || "—")}</div></div>
    <div class="box"><div class="lbl">Jurisdicción</div><div class="val">${esc(parte?.jurisdiccion || "—")}</div></div>
    <div class="box"><div class="lbl">Comuna</div><div class="val">${esc(parte?.comuna || "—")}</div></div>
    <div class="box"><div class="lbl">Zona</div><div class="val">${esc(zonaNice || "—")}</div></div>
    <div class="box"><div class="lbl">Operadora despacho</div><div class="val">${esc(operadoraTxt || "—")}</div></div>
  </div>

  <div class="section">
    <h3>Compañías despachadas</h3>
    <div class="box">
      <div class="p">${esc(companiasNombres.join(", ") || "—")}</div>
    </div>
  </div>

  <div class="section">
    <h3>Realiza parte</h3>
    ${realizaHtml}
  </div>

  <div class="section">
    <h3>OBAC Cuerpo</h3>
    ${obacCuerpoHtml}
  </div>

  <div class="section">
    <h3>Unidades</h3>
    ${unidadesHtml}
  </div>

  <div class="section">
    <h3>Asistentes</h3>
    ${asistentesHtml}
  </div>

  <div class="section">
    <h3>Observaciones</h3>
    <div class="box"><div class="p">${esc(parte?.observaciones || "—")}</div></div>
  </div>

  <div class="section">
    <h3>Detalle específico</h3>
    ${detalleHtml}
  </div>
</body>
</html>`;

  openPrintWindow({ title: "Parte", html });
}

  function generarInformePDF() {
    if (partesFiltrados.length === 0) return;

    // Reglas: informe/gráfico => logo según scope
    const logo = logoPathForScope(scope);
    const titulo = headerTitle(scope, cuerpoNombre);

    const filas = partesFiltrados
      .map((p) => {
        const ymd = p?.fechaEmergencia || ymdFromAny(p?.createdAt);
        const tipo = p?.tipoEmergenciaNombre || p?.tipo_emergencia_nombre || "—";
        const comps = (p?.companias_despachadas_nombres || []).join(", ");
        return `<tr>
          <td>${p?.correlativo || "—"}</td>
          <td>${ymd || "—"} ${p?.horaDespacho ? " " + p.horaDespacho : ""}</td>
          <td>${tipo}</td>
          <td>${p?.direccion || "—"}</td>
          <td>${p?.comuna || "—"}</td>
          <td>${comps || "—"}</td>
          <td style="text-align:right">${countAsistentesForScope(p, scope)}</td>
        </tr>`;
      })
      .join("");

    const html = `<!doctype html>
<html>
<head>
<meta charset="utf-8" />
<title>Informe ${anio}</title>
<style>
  body{font-family: ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Arial; padding:28px; color:#0b1220;}
  .head{display:flex; gap:16px; align-items:center; border-bottom:2px solid #e5e7eb; padding-bottom:14px; margin-bottom:18px;}
  .logo{width:68px; height:68px; object-fit:contain;}
  .t1{font-weight:900; font-size:18px; margin:0;}
  .t2{margin:4px 0 0; color:#334155; font-size:12px;}
  .meta{display:flex; justify-content:space-between; margin:14px 0 10px; color:#334155; font-size:12px;}
  table{width:100%; border-collapse:collapse; overflow:hidden; border-radius:12px;}
  th,td{border-bottom:1px solid #e5e7eb; padding:10px 10px; font-size:12px; vertical-align:top;}
  th{background:#0b1220; color:white; text-align:left; font-weight:900;}
  tr:nth-child(even) td{background:#f8fafc;}
  .foot{margin-top:12px; font-size:11px; color:#64748b;}
</style>
</head>
<body>
  <div class="head">
    <img class="logo" src="${logo}" />
    <div>
      <p class="t1">${titulo}</p>
      <p class="t2">Año: <b>${anio}</b> · Partes: <b>${partesFiltrados.length}</b></p>
    </div>
  </div>

  <div class="meta">
    <div>Filtros: Mes <b>${mes === "all" ? "Todos" : mes}</b> · Tipo <b>${tipo === "all" ? "Todos" : tipo}</b></div>
    <div>Generado: <b>${new Date().toLocaleString("es-CL")}</b></div>
  </div>

  <table>
    <thead>
      <tr>
        <th>Correlativo</th>
        <th>Fecha/Hora</th>
        <th>Tipo</th>
        <th>Dirección</th>
        <th>Comuna</th>
        <th>Compañías</th>
        <th style="text-align:right">Asistentes</th>
      </tr>
    </thead>
    <tbody>${filas}</tbody>
  </table>

  <div class="foot">Digital Evolution · Reporte generado desde el módulo de Partes de Emergencia.</div>

  
</body>
</html>`;
    openPrintWindow({ title: "Informe", html });
  }

  function generarGraficosPDF() {
  if (partesFiltrados.length === 0) return;

  const logo = logoPathForScope(scope);
  const titulo = headerTitle(scope, cuerpoNombre);

  const esc = (v) =>
    String(v ?? "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");

  const mesesLabels = ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"];

  const palette = [
    "#0f172a", "#2563eb", "#dc2626", "#16a34a", "#d97706", "#7c3aed",
    "#0891b2", "#be123c", "#65a30d", "#4338ca", "#ea580c", "#0f766e",
    "#a21caf", "#1d4ed8", "#b91c1c", "#15803d", "#92400e", "#6d28d9"
  ];

  const getColor = (idx) => palette[idx % palette.length];
  const normalizeText = (v) => String(v || "").trim();

  const extractClavePrincipal = (parte) => {
    const candidates = [
      parte?.tipoEmergenciaId,
      parte?.tipo_emergencia_id,
      parte?.tipoEmergenciaNombre,
      parte?.tipo_emergencia_nombre,
      parte?.tipo,
    ].filter(Boolean);

    for (const raw of candidates) {
      const txt = String(raw).trim();
      const m = txt.match(/\b\d{1,2}-\d{1,2}\b/);
      if (m) return m[0];
    }

    const fallback =
      parte?.tipoEmergenciaNombre ||
      parte?.tipo_emergencia_nombre ||
      parte?.tipoEmergenciaId ||
      parte?.tipo_emergencia_id ||
      "Sin tipo";

    return normalizeText(fallback);
  };

  const extractSubclave = (parte) => {
    const candidates = [
      parte?.subTipoEmergenciaId,
      parte?.sub_tipo_emergencia_id,
      parte?.subTipoEmergenciaNombre,
      parte?.sub_tipo_emergencia_nombre,
      parte?.subclave,
    ].filter(Boolean);

    for (const raw of candidates) {
      const txt = String(raw).trim();
      const m3 = txt.match(/\b\d{1,2}-\d{1,2}-\d{1,2}\b/);
      if (m3) return m3[0];
    }

    for (const raw of candidates) {
      const txt = String(raw).trim();
      if (txt) return txt;
    }

    return "Sin subclave";
  };

  const byMonth = Array.from({ length: 12 }, (_, i) => ({
    m: i + 1,
    partes: 0,
    asistentes: 0,
  }));

  const principalesCount = {};
  const subclavesCount = {};
  const principalesPorMes = {};
  const subclavesPorMes = {};

  for (const p of partesFiltrados) {
    const ymd = p?.fechaEmergencia || ymdFromAny(p?.createdAt);
    const m = monthFromYmd(ymd);
    const principal = extractClavePrincipal(p);
    const subclave = extractSubclave(p);

    principalesCount[principal] = (principalesCount[principal] || 0) + 1;
    subclavesCount[subclave] = (subclavesCount[subclave] || 0) + 1;

    if (!principalesPorMes[principal]) principalesPorMes[principal] = Array(12).fill(0);
    if (!subclavesPorMes[subclave]) subclavesPorMes[subclave] = Array(12).fill(0);

    if (m >= 1 && m <= 12) {
      byMonth[m - 1].partes += 1;
      byMonth[m - 1].asistentes += Number(countAsistentesForScope(p, scope));
      principalesPorMes[principal][m - 1] += 1;
      subclavesPorMes[subclave][m - 1] += 1;
    }
  }

  const totalAnual = byMonth.reduce((a, b) => a + b.partes, 0);
  const totalAsist = byMonth.reduce((a, b) => a + b.asistentes, 0);

  const principalEntries = Object.entries(principalesCount)
    .filter(([, v]) => Number(v) > 0)
    .sort((a, b) => b[1] - a[1]);

  const subclaveEntries = Object.entries(subclavesCount)
    .filter(([, v]) => Number(v) > 0)
    .sort((a, b) => b[1] - a[1]);

  const renderBarChartSVG = ({
    title,
    data,
    valueKey,
    color = "#0b1220",
    width = 980,
    height = 300,
  }) => {
    const margin = { top: 26, right: 20, bottom: 54, left: 48 };
    const chartW = width - margin.left - margin.right;
    const chartH = height - margin.top - margin.bottom;

    const maxValue = Math.max(1, ...data.map((d) => Number(d[valueKey] || 0)));
    const stepX = chartW / data.length;
    const barW = Math.min(40, stepX * 0.54);

    const yTicks = 5;
    const tickValues = Array.from({ length: yTicks + 1 }, (_, i) =>
      Math.round((maxValue / yTicks) * i)
    );

    const gridLines = tickValues
      .map((tick) => {
        const y = margin.top + chartH - (tick / maxValue) * chartH;
        return `
          <line x1="${margin.left}" y1="${y}" x2="${margin.left + chartW}" y2="${y}" stroke="#e5e7eb" stroke-width="1" />
          <text x="${margin.left - 8}" y="${y + 4}" text-anchor="end" font-size="10" fill="#64748b" font-weight="700">${tick}</text>
        `;
      })
      .join("");

    const bars = data
      .map((d, i) => {
        const value = Number(d[valueKey] || 0);
        const barH = maxValue > 0 ? (value / maxValue) * chartH : 0;
        const x = margin.left + i * stepX + (stepX - barW) / 2;
        const y = margin.top + chartH - barH;
        const label = mesesLabels[i] || String(d.m);

        return `
          <rect x="${x}" y="${y}" width="${barW}" height="${barH}" rx="6" ry="6" fill="${color}" />
          <text x="${x + barW / 2}" y="${y - 7}" text-anchor="middle" font-size="10" fill="#0f172a" font-weight="800">${value}</text>
          <text x="${x + barW / 2}" y="${margin.top + chartH + 18}" text-anchor="middle" font-size="10" fill="#475569" font-weight="800">${label}</text>
        `;
      })
      .join("");

    return `
      <section class="chart-block">
        <div class="card">
          <p class="h">${esc(title)}</p>
          <svg width="100%" viewBox="0 0 ${width} ${height}" role="img" aria-label="${esc(title)}">
            <rect x="0" y="0" width="${width}" height="${height}" fill="#ffffff"></rect>
            ${gridLines}
            <line x1="${margin.left}" y1="${margin.top + chartH}" x2="${margin.left + chartW}" y2="${margin.top + chartH}" stroke="#94a3b8" stroke-width="1.2" />
            <line x1="${margin.left}" y1="${margin.top}" x2="${margin.left}" y2="${margin.top + chartH}" stroke="#94a3b8" stroke-width="1.2" />
            ${bars}
          </svg>
        </div>
      </section>
    `;
  };

  const polarToCartesian = (cx, cy, r, angleDeg) => {
    const angleRad = (angleDeg - 90) * Math.PI / 180.0;
    return {
      x: cx + (r * Math.cos(angleRad)),
      y: cy + (r * Math.sin(angleRad)),
    };
  };

  const describeArc = (cx, cy, r, startAngle, endAngle) => {
    const start = polarToCartesian(cx, cy, r, endAngle);
    const end = polarToCartesian(cx, cy, r, startAngle);
    const largeArcFlag = endAngle - startAngle <= 180 ? "0" : "1";
    return `M ${cx} ${cy} L ${start.x} ${start.y} A ${r} ${r} 0 ${largeArcFlag} 0 ${end.x} ${end.y} Z`;
  };

  const renderPieChartSVG = ({
    title,
    entries,
    width = 900,
    height = 260,
    forceNewPage = false,
  }) => {
    if (!entries.length) {
      return `
        <section class="chart-block ${forceNewPage ? "page-start" : ""}">
          <div class="card">
            <p class="h">${esc(title)}</p>
            <div class="muted">— Sin datos —</div>
          </div>
        </section>
      `;
    }

    const total = entries.reduce((a, [, v]) => a + Number(v || 0), 0);
    const cx = 145;
    const cy = 130;
    const r = 88;
    let currentAngle = 0;

    const slices = entries.map(([label, value], idx) => {
      const portion = total > 0 ? Number(value) / total : 0;
      const angle = portion * 360;
      const startAngle = currentAngle;
      const endAngle = currentAngle + angle;
      currentAngle += angle;

      const path = describeArc(cx, cy, r, startAngle, endAngle);
      const mid = startAngle + angle / 2;
      const textPoint = polarToCartesian(cx, cy, r * 0.68, mid);
      const percent = total > 0 ? ((Number(value) / total) * 100).toFixed(1) : "0.0";
      const fill = getColor(idx);

      return {
        path,
        fill,
        value,
        label,
        percent,
        textX: textPoint.x,
        textY: textPoint.y,
      };
    });

    const svgSlices = slices.map((s) => `
      <path d="${s.path}" fill="${s.fill}" stroke="#ffffff" stroke-width="2"></path>
    `).join("");

    const svgTexts = slices
      .filter((s) => Number(s.percent) >= 8)
      .map((s) => `
        <text x="${s.textX}" y="${s.textY}" text-anchor="middle" font-size="10" fill="#ffffff" font-weight="900">${esc(s.percent)}%</text>
      `)
      .join("");

    const legend = slices.map((s) => `
      <div class="legend-item">
        <span class="dot" style="background:${s.fill}"></span>
        <span class="legend-label">${esc(s.label)}</span>
        <span class="legend-value">${esc(s.value)} (${esc(s.percent)}%)</span>
      </div>
    `).join("");

    return `
      <section class="chart-block chart-pie ${forceNewPage ? "page-start" : ""}">
        <div class="card">
          <p class="h">${esc(title)}</p>
          <div class="pie-wrap">
            <div class="pie-figure">
              <svg class="pie-svg" viewBox="0 0 ${width} ${height}" role="img" aria-label="${esc(title)}">
                <rect x="0" y="0" width="${width}" height="${height}" fill="#ffffff"></rect>
                ${svgSlices}
                ${svgTexts}
              </svg>
            </div>
            <div class="legend">
              ${legend}
            </div>
          </div>
        </div>
      </section>
    `;
  };

  const renderStackedMonthlyChartSVG = ({
    title,
    seriesMap,
    width = 980,
    height = 300,
    forceNewPage = false,
  }) => {
    const entries = Object.entries(seriesMap)
      .filter(([, arr]) => Array.isArray(arr) && arr.some((n) => Number(n) > 0))
      .sort((a, b) => {
        const ta = a[1].reduce((s, n) => s + Number(n || 0), 0);
        const tb = b[1].reduce((s, n) => s + Number(n || 0), 0);
        return tb - ta;
      });

    if (!entries.length) {
      return `
        <section class="chart-block ${forceNewPage ? "page-start" : ""}">
          <div class="card">
            <p class="h">${esc(title)}</p>
            <div class="muted">— Sin datos —</div>
          </div>
        </section>
      `;
    }

    const margin = { top: 24, right: 20, bottom: 48, left: 46 };
    const chartW = width - margin.left - margin.right;
    const chartH = height - margin.top - margin.bottom;

    const monthTotals = Array.from({ length: 12 }, (_, i) =>
      entries.reduce((sum, [, arr]) => sum + Number(arr[i] || 0), 0)
    );
    const maxValue = Math.max(1, ...monthTotals);

    const stepX = chartW / 12;
    const barW = Math.min(38, stepX * 0.52);

    const yTicks = 5;
    const tickValues = Array.from({ length: yTicks + 1 }, (_, i) =>
      Math.round((maxValue / yTicks) * i)
    );

    const gridLines = tickValues
      .map((tick) => {
        const y = margin.top + chartH - (tick / maxValue) * chartH;
        return `
          <line x1="${margin.left}" y1="${y}" x2="${margin.left + chartW}" y2="${y}" stroke="#e5e7eb" stroke-width="1" />
          <text x="${margin.left - 8}" y="${y + 4}" text-anchor="end" font-size="10" fill="#64748b" font-weight="700">${tick}</text>
        `;
      })
      .join("");

    const bars = Array.from({ length: 12 }, (_, monthIdx) => {
      const x = margin.left + monthIdx * stepX + (stepX - barW) / 2;
      let currentHeight = 0;

      const segments = entries.map(([, arr], seriesIdx) => {
        const value = Number(arr[monthIdx] || 0);
        if (!value) return "";

        const segH = (value / maxValue) * chartH;
        const y = margin.top + chartH - currentHeight - segH;
        currentHeight += segH;

        return `<rect x="${x}" y="${y}" width="${barW}" height="${segH}" fill="${getColor(seriesIdx)}"></rect>`;
      }).join("");

      const total = monthTotals[monthIdx] || 0;
      const totalY = margin.top + chartH - ((total / maxValue) * chartH) - 6;

      return `
        ${segments}
        <text x="${x + barW / 2}" y="${totalY}" text-anchor="middle" font-size="10" fill="#0f172a" font-weight="800">${total}</text>
        <text x="${x + barW / 2}" y="${margin.top + chartH + 17}" text-anchor="middle" font-size="10" fill="#475569" font-weight="800">${mesesLabels[monthIdx]}</text>
      `;
    }).join("");

    const legend = entries.map(([label, arr], idx) => {
      const total = arr.reduce((s, n) => s + Number(n || 0), 0);
      return `
        <div class="legend-item">
          <span class="dot" style="background:${getColor(idx)}"></span>
          <span class="legend-label">${esc(label)}</span>
          <span class="legend-value">${esc(total)}</span>
        </div>
      `;
    }).join("");

    return `
      <section class="chart-block ${forceNewPage ? "page-start" : ""}">
        <div class="card">
          <p class="h">${esc(title)}</p>
          <svg width="100%" viewBox="0 0 ${width} ${height}" role="img" aria-label="${esc(title)}">
            <rect x="0" y="0" width="${width}" height="${height}" fill="#ffffff"></rect>
            ${gridLines}
            <line x1="${margin.left}" y1="${margin.top + chartH}" x2="${margin.left + chartW}" y2="${margin.top + chartH}" stroke="#94a3b8" stroke-width="1.2" />
            <line x1="${margin.left}" y1="${margin.top}" x2="${margin.left}" y2="${margin.top + chartH}" stroke="#94a3b8" stroke-width="1.2" />
            ${bars}
          </svg>
          <div class="legend legend-compact" style="margin-top:10px">
            ${legend}
          </div>
        </div>
      </section>
    `;
  };

  const chartPartes = renderBarChartSVG({
    title: "Emergencias por mes",
    data: byMonth,
    valueKey: "partes",
    color: "#0f172a",
  });

  const chartAsistencias = renderBarChartSVG({
    title: "Asistencia de bomberos por mes",
    data: byMonth,
    valueKey: "asistentes",
    color: "#2563eb",
  });

  const chartPiePrincipal = renderPieChartSVG({
    title: "Emergencias anuales por clave principal",
    entries: principalEntries,
    forceNewPage: true,
  });

  const chartPieSubclave = renderPieChartSVG({
    title: "Emergencias anuales por subclave",
    entries: subclaveEntries,
  });

  const chartMensualPrincipal = renderStackedMonthlyChartSVG({
    title: "Emergencias mensuales por clave principal",
    seriesMap: principalesPorMes,
    forceNewPage: true,
  });

  const chartMensualSubclave = renderStackedMonthlyChartSVG({
    title: "Emergencias mensuales por subclave",
    seriesMap: subclavesPorMes,
  });

  const tablaResumen = `
    <table class="tbl">
      <thead>
        <tr>
          <th>Mes</th>
          <th>Cantidad de Emergencias</th>
          <th>Cantidad de Bomberos</th>
        </tr>
      </thead>
      <tbody>
        ${byMonth.map((x, i) => `
          <tr>
            <td>${mesesLabels[i]}</td>
            <td>${x.partes}</td>
            <td>${x.asistentes}</td>
          </tr>
        `).join("")}
      </tbody>
    </table>
  `;

  const tablaPrincipales = `
    <table class="tbl">
      <thead>
        <tr>
          <th>Clave principal</th>
          <th>Total Emergencias Anuales</th>
        </tr>
      </thead>
      <tbody>
        ${principalEntries.length
          ? principalEntries.map(([k, v]) => `
            <tr>
              <td>${esc(k)}</td>
              <td>${esc(v)}</td>
            </tr>
          `).join("")
          : `<tr><td colspan="2">—</td></tr>`
        }
      </tbody>
    </table>
  `;

  const tablaSubclaves = `
    <table class="tbl">
      <thead>
        <tr>
          <th>Subclave</th>
          <th>Total Emergencias Anuales</th>
        </tr>
      </thead>
      <tbody>
        ${subclaveEntries.length
          ? subclaveEntries.map(([k, v]) => `
            <tr>
              <td>${esc(k)}</td>
              <td>${esc(v)}</td>
            </tr>
          `).join("")
          : `<tr><td colspan="2">—</td></tr>`
        }
      </tbody>
    </table>
  `;

  const html = `<!doctype html>
<html>
<head>
<meta charset="utf-8" />
<title>Gráficos ${esc(anio)}</title>
<style>
  body{
    font-family: ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Arial;
    padding: 18px 20px;
    color:#0b1220;
    background:#fff;
  }
  .head{
    display:flex;
    gap:14px;
    align-items:center;
    border-bottom:2px solid #e5e7eb;
    padding-bottom:12px;
    margin-bottom:14px;
  }
  .logo{
    width:60px;
    height:60px;
    object-fit:contain;
  }
  .t1{
    font-weight:900;
    font-size:17px;
    margin:0;
  }
  .t2{
    margin:4px 0 0;
    color:#334155;
    font-size:11px;
  }
  .kpis{
    display:flex;
    gap:10px;
    flex-wrap:wrap;
    margin-top:8px;
    margin-bottom:12px;
  }
  .kpi{
    border:1px solid #e5e7eb;
    border-radius:12px;
    padding:8px 10px;
    background:white;
    min-width:170px;
  }
  .kpi .l{
    font-size:10px;
    color:#64748b;
    font-weight:900;
  }
  .kpi .v{
    font-size:17px;
    font-weight:1000;
    margin-top:3px;
  }

  .chart-block{
    margin-bottom:12px;
    break-inside:avoid-page;
    page-break-inside:avoid;
  }
  .page-start{
    break-before:page;
    page-break-before:always;
  }

  .card{
    border:1px solid #e5e7eb;
    border-radius:14px;
    padding:12px;
    background:#fff;
    break-inside:avoid-page;
    page-break-inside:avoid;
    overflow:visible;
  }
  .h{
    font-weight:900;
    margin:0 0 8px;
    font-size:12px;
    letter-spacing:.02em;
    text-transform:uppercase;
  }
  .tbl{
    width:100%;
    border-collapse:collapse;
    font-size:11px;
    margin-top:4px;
  }
  .tbl th,.tbl td{
    border:1px solid #e5e7eb;
    padding:7px;
    vertical-align:top;
    text-align:left;
  }
  .tbl th{
    background:#f8fafc;
  }
  .muted{
    color:#64748b;
  }

  .pie-wrap{
    display:grid;
    grid-template-columns: 1fr;
    gap:10px;
    align-items:start;
  }
  .pie-figure{
    width:100%;
  }
  .pie-svg{
    width:100%;
    height:auto;
    display:block;
    max-height:220px;
  }

  .legend{
    display:grid;
    gap:6px;
    align-content:start;
  }
  .legend-compact{
    grid-template-columns: repeat(2, minmax(0, 1fr));
    column-gap:12px;
  }
  .legend-item{
    display:grid;
    grid-template-columns:12px 1fr auto;
    gap:7px;
    align-items:center;
    font-size:11px;
  }
  .dot{
    width:12px;
    height:12px;
    border-radius:999px;
    display:inline-block;
  }
  .legend-label{
    color:#0f172a;
    font-weight:700;
  }
  .legend-value{
    color:#475569;
    font-weight:800;
  }

  .summary-block{
    break-inside:avoid-page;
    page-break-inside:avoid;
    margin-bottom:12px;
  }

  @media screen and (min-width: 900px) {
    .pie-wrap{
      grid-template-columns: 320px 1fr;
      gap:14px;
      align-items:center;
    }
    .pie-svg{
      max-height:240px;
    }
  }

  @media print {
    html, body{
      width:100%;
      height:auto;
    }
    body{
      padding: 8px 10px;
    }
    .chart-block,
    .card,
    .summary-block{
      break-inside:avoid-page !important;
      page-break-inside:avoid !important;
    }
    .pie-wrap{
      grid-template-columns: 1fr !important;
      gap:8px !important;
      align-items:start !important;
    }
    .pie-svg{
      max-height:190px !important;
    }
    .legend{
      gap:5px;
    }
    .legend-item{
      font-size:10px;
      grid-template-columns:10px 1fr auto;
      gap:6px;
    }
    .dot{
      width:10px;
      height:10px;
    }
    .legend-compact{
      grid-template-columns: repeat(2, minmax(0, 1fr));
      column-gap:10px;
    }
    .tbl{
      font-size:10px;
    }
    .tbl th,.tbl td{
      padding:6px;
    }
  }

  @page{
    size:A4 portrait;
    margin:16mm 10mm 16mm 10mm;
  }

  *{
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
    box-sizing:border-box;
  }
</style>
</head>
<body>
  <div class="head">
    <img class="logo" src="${logo}" />
    <div>
      <p class="t1">${esc(titulo)}</p>
      <p class="t2">Gráficos (según filtros) · Año: <b>${esc(anio)}</b></p>
    </div>
  </div>

  <div class="kpis">
    <div class="kpi"><div class="l">Total anual (partes)</div><div class="v">${totalAnual}</div></div>
    <div class="kpi"><div class="l">Total anual (asistencias)</div><div class="v">${totalAsist}</div></div>
  </div>

  ${chartPartes}
  ${chartAsistencias}
  ${chartPiePrincipal}
  ${chartPieSubclave}
  ${chartMensualPrincipal}
  ${chartMensualSubclave}

  <section class="summary-block page-start">
    <div class="card">
      <p class="h">Resumen mensual</p>
      ${tablaResumen}
    </div>
  </section>

  <section class="summary-block">
    <div class="card">
      <p class="h">Resumen anual por clave principal</p>
      ${tablaPrincipales}
    </div>
  </section>

  <section class="summary-block">
    <div class="card">
      <p class="h">Resumen anual por subclave</p>
      ${tablaSubclaves}
    </div>
  </section>
</body>
</html>`;

  openPrintWindow({ title: "Graficos", html });
}

  async function generarInformeAsistenciaPDF() {
    if (partesFiltrados.length === 0) return;

    const logo = logoPathForScope(scope);
    const titulo = headerTitle(scope, cuerpoNombre);

    // Partes relevantes según alcance:
let partesAlcance = partesFiltrados;

// Helper local: obtiene nombres de compañías despachadas desde el documento (tolerante a formatos)
const getCompaniasNombres = (p) => {
  let nombres =
    p?.companias_despachadas_nombres ??
    p?.companiasDespachadasNombres ??
    p?.companias_despachadasNombres ??
    p?.companiasNombres ??
    p?.companias ??
    p?.compañias ??
    [];

  if (typeof nombres === "string") {
    nombres = nombres.split(/[,/|]+/).map((s) => String(s || "").trim()).filter(Boolean);
  } else if (nombres && typeof nombres === "object" && !Array.isArray(nombres)) {
    nombres = Object.values(nombres).map(String).filter(Boolean);
  }

  return Array.isArray(nombres) ? nombres.filter(Boolean).map(String) : [];
};

if (scope.mode === "compania") {
  const cNombre = scope?.nombre || "";
  const cId = scope?.id || "";

  partesAlcance = partesFiltrados.filter((p) => {
    const ids =
      p?.companias_despachadas_ids ||
      p?.companiasDespachadasIds ||
      p?.companias_despachadasIds ||
      p?.companiasIds ||
      [];

    const noms = getCompaniasNombres(p);

    const okId = cId && Array.isArray(ids) && ids.includes(cId);
    const okNombre = cNombre && noms.some((n) => matchCompania(n, cNombre));
    return okId || okNombre;
  });
}

// ✅ Total de despachos por compañía (siempre desde los filtros generales, NO desde el alcance)
// Esto asegura que el % de cada bombero no cambie al generar por cuerpo o por compañía.
const totalDespachosPorCompania = {};
for (const p of partesFiltrados) {
  const noms = getCompaniasNombres(p);
  for (const n of noms) {
    const k = normalizeText(n);
    if (!k) continue;
    totalDespachosPorCompania[k] = (totalDespachosPorCompania[k] || 0) + 1;
  }
}

    // Cargar bomberos según alcance
    let bomberos = [];
    try {
      if (scope.mode === "compania" && scope.nombre) {
        const snap = await getDocs(query(collection(db, "bomberos"), where("compania", "==", scope.nombre)));
        bomberos = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      } else {
        const snap = await getDocs(collection(db, "bomberos"));
        bomberos = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      }
    } catch (e) {
      console.warn("⚠️ No se pudieron cargar bomberos:", e);
      bomberos = [];
    }

    // totales por mes para despachos
    const despachosMes = Array.from({ length: 12 }, (_, i) => ({ m: i + 1, total: 0 }));
    for (const p of partesAlcance) {
      const ymd = p?.fechaEmergencia || ymdFromAny(p?.createdAt);
      const m = monthFromYmd(ymd);
      if (m >= 1 && m <= 12) despachosMes[m - 1].total += 1;
    }

    // asistencias por bombero (anual) + porcentaje
    const stats = bomberos
  .map((b) => {
    const id = b.id;
    const nombre = b?.nombre || b?.nombreCompleto || b?.nombres || "—";
    const compania = b?.compania || "";

    // Partes donde fue despachada la compañía del bombero (según filtros generales)
    const partesDeSuCompania = partesFiltrados.filter((p) => {
      const noms =
        p?.companias_despachadas_nombres ??
        p?.companiasDespachadasNombres ??
        p?.companias_despachadasNombres ??
        p?.companiasNombres ??
        p?.companias ??
        p?.compañias ??
        [];
      const arr = typeof noms === "string"
        ? noms.split(/[,/|]+/).map((s) => String(s || "").trim()).filter(Boolean)
        : (Array.isArray(noms) ? noms : (noms && typeof noms === "object" ? Object.values(noms).map(String).filter(Boolean) : []));
      return compania && arr.some((n) => matchCompania(n, compania));
    });

    // ✅ Asistencias del bombero: contar en cuántos PARTES (de su compañía) aparece él como asistente.
// Importante: NO sumar el total de asistentes de la compañía, sino si ESTE bombero asistió (0/1 por parte).
const bomberoKey = normalizeText(b?.id || b?.rut || b?.RUT || b?.idBombero || "");
const asistioEnParte = (p) => {
  // 1) por asistentesIds (rápido)
  const ids = Array.isArray(p?.asistentesIds) ? p.asistentesIds : [];
  if (bomberoKey && ids.some((x) => normalizeText(x) === bomberoKey)) return true;

  // 2) por detalle (asistentesSnapshot / asistentes / etc.)
  const arr = getAsistentesArray(p);
  return arr.some((a) => {
    const ak = normalizeText(a?.id || a?.rut || a?.RUT || a?.bomberoId || a?.uid || "");
    return bomberoKey && ak === bomberoKey;
  });
};

const asist = partesDeSuCompania.reduce((acc, p) => acc + (asistioEnParte(p) ? 1 : 0), 0);

    const desp = totalDespachosPorCompania[normalizeText(compania)] || partesDeSuCompania.length || 0;

    const pct = desp > 0 ? Math.round((asist / desp) * 1000) / 10 : 0; // 1 decimal

    return { id, nombre, compania, asistencias: asist, despachos: desp, porcentaje: pct };
  })
  .filter((x) => x && x.compania);

// Orden: por compañía ascendente (Primera, Segunda, ...) y dentro por mayor porcentaje/asistencias
stats.sort((a, b) => {
  const na = getCompaniaNumberFromNombre(a.compania) || 999;
  const nb = getCompaniaNumberFromNombre(b.compania) || 999;
  if (na !== nb) return na - nb;

  if (b.porcentaje !== a.porcentaje) return b.porcentaje - a.porcentaje;
  if (b.asistencias !== a.asistencias) return b.asistencias - a.asistencias;
  return String(a.nombre).localeCompare(String(b.nombre));
});




    const rows = stats
      .slice(0, 60)
      .map(
        (s, i) => `<tr>
          <td style="width:40px; text-align:right">${i + 1}</td>
          <td><b>${s.nombre}</b>${s.compania ? `<div style="color:#64748b; font-size:11px">${s.compania}</div>` : ""}</td>
          <td style="text-align:right">${s.asistencias}</td>
          <td style="text-align:right">${s.despachos}</td>
          <td style="text-align:right">${s.porcentaje}%</td>
        </tr>`
      )
      .join("");

    const resumenMes = despachosMes
      .map((x) => `<div class="kpi"><div class="l">Mes ${x.m}</div><div class="v">${x.total}</div></div>`)
      .join("");

    const html = `<!doctype html>
<html>
<head>
<meta charset="utf-8" />
<title>Informe Asistencia ${anio}</title>
<style>
  body{font-family: ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Arial; padding:28px; color:#0b1220;}
  .head{display:flex; gap:16px; align-items:center; border-bottom:2px solid #e5e7eb; padding-bottom:14px; margin-bottom:18px;}
  .logo{width:68px; height:68px; object-fit:contain;}
  .t1{font-weight:900; font-size:18px; margin:0;}
  .t2{margin:4px 0 0; color:#334155; font-size:12px;}
  .kpis{display:flex; gap:10px; flex-wrap:wrap; margin:12px 0 16px;}
  .kpi{border:1px solid #e5e7eb; border-radius:12px; padding:10px 12px; background:white; min-width:120px;}
  .kpi .l{font-size:11px; color:#64748b; font-weight:900;}
  .kpi .v{font-size:16px; font-weight:1000; margin-top:4px;}
  table{width:100%; border-collapse:collapse; overflow:hidden; border-radius:12px;}
  th,td{border-bottom:1px solid #e5e7eb; padding:10px 10px; font-size:12px; vertical-align:top;}
  th{background:#0b1220; color:white; text-align:left; font-weight:900;}
  tr:nth-child(even) td{background:#f8fafc;}
  .foot{margin-top:12px; font-size:11px; color:#64748b;}
</style>
</head>
<body>
  <div class="head">
    <img class="logo" src="${logo}" />
    <div>
      <p class="t1">${titulo}</p>
      <p class="t2">Informe de asistencia (según filtros) · Año: <b>${anio}</b> · Partes considerados: <b>${partesAlcance.length}</b></p>
    </div>
  </div>

  <div class="kpis">
    <div class="kpi"><div class="l">Total partes</div><div class="v">${partesAlcance.length}</div></div>
    <div class="kpi"><div class="l">Total bomberos</div><div class="v">${bomberos.length}</div></div>
    ${resumenMes}
  </div>

  <table>
    <thead>
      <tr>
        <th style="text-align:right">#</th>
        <th>Bombero</th>
        <th style="text-align:right">Asistencias</th>
        <th style="text-align:right">Despachos</th>
        <th style="text-align:right">% Asistencia</th>
      </tr>
    </thead>
    <tbody>${rows || ""}</tbody>
  </table>

  <div class="foot">Nota: % Asistencia = asistencias / despachos considerados según filtros.</div>
  
</body>
</html>`;

    openPrintWindow({ title: "Informe Asistencia", html });
  }

  const resultados = partesFiltrados.length;

  // UI overflow fix: grids responsive
  const monthOptions = [
    { v: "all", t: "— Todos —" },
    ...Array.from({ length: 12 }, (_, i) => ({ v: String(i + 1), t: String(i + 1).padStart(2, "0") })),
  ];

  if (loading) {
    return (
      <div style={S.embedWrap}>
        <div style={S.card}>Cargando…</div>
      </div>
    );
  }

  if (!user) {
    return (
      <div style={S.embedWrap}>
        <div style={S.card}>
          <b>Sesión finalizada.</b>
          <div style={{ marginTop: 10 }}>
            <button style={S.btn} onClick={() => navigate("/login")}>Ir a login</button>
          </div>
        </div>
      </div>
    );
  }

  const scopeReady = scopeMode === "cuerpo" || (scopeMode === "compania" && scopeCompaniaId);

  return (
    <div style={S.embedWrap}>
      {/* ✅ EN CREACIÓN */}
      {enCreacion.length > 0 && (
        <div style={{ ...S.card, border: "1px solid #fde68a", background: "#fffbeb" }}>
          <div style={{ fontWeight: 1000, marginBottom: 8 }}>🟡 Partes en creación</div>
          <div style={{ display: "grid", gap: 8 }}>
            {enCreacion
              .sort((a, b) => {
                const ta = a?.updated_at?.toMillis?.() || a?.expires_at?.toMillis?.() || 0;
                const tb = b?.updated_at?.toMillis?.() || b?.expires_at?.toMillis?.() || 0;
                return tb - ta;
              })
              .map((x) => (
                <div key={x.id} style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
                  <div style={{ fontWeight: 900 }}>{x.correlativo ? x.correlativo : `EN CREACIÓN · ${String(x.id).slice(0, 10)}…`}</div>
                  <div style={{ color: "#334155" }}>
                    {x?.tipo_emergencia_nombre || "—"} · {x?.creado_por_email || "—"}
                  </div>
                  <div style={{ marginLeft: "auto", color: "#b45309", fontWeight: 800 }}>
                    expira: {fmtDate(x?.expires_at)}
                  </div>
                </div>
              ))}
          </div>
        </div>
      )}

      {/* Filtros + export */}
      <div style={S.card}>
        <div style={S.filtersGrid}>
          <div style={S.field}>
            <label style={S.label}>Año</label>
            <select value={anio} onChange={(e) => setAnio(Number(e.target.value))} style={S.input}>
              {Array.from({ length: 8 }, (_, i) => new Date().getFullYear() - i).map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
            <div style={S.hint}>Para ver años anteriores, selecciona el año.</div>
          </div>

          <div style={S.field}>
            <label style={S.label}>Mes</label>
            <select value={mes} onChange={(e) => setMes(e.target.value)} style={S.input}>
              {monthOptions.map((o) => (
                <option key={o.v} value={o.v}>{o.t}</option>
              ))}
            </select>
            <div style={S.hint}>Filtra por mes según fecha del llamado.</div>
          </div>

          <div style={S.field}>
            <label style={S.label}>Tipo de emergencia</label>
            <select value={tipo} onChange={(e) => setTipo(e.target.value)} style={S.input}>
              <option value="all">— Todos —</option>
              {tiposDisponibles.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
            <div style={S.hint}>Usa “Todos” para quitar el filtro.</div>
          </div>

          <div style={S.field}>
            <label style={S.label}>Fecha específica</label>
            <input type="date" value={fechaExacta} onChange={(e) => setFechaExacta(e.target.value)} style={S.input} />
            <div style={S.hint}>Filtra por una fecha exacta.</div>
          </div>

          <div style={{ ...S.field, gridColumn: "1 / -1" }}>
            <label style={S.label}>Búsqueda libre</label>
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Ej: 0004-2026, Huasco, 10-0-1..."
              style={S.input}
            />
            <div style={S.hint}>Filtra sin consultar a Firestore (instantáneo).</div>
          </div>
        </div>

        {/* Alcance para informes / gráficos */}
        <div style={S.scopeRow}>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center", minWidth: 0 }}>
            <div style={{ fontWeight: 1000 }}>Alcance para Informe/Gráfico:</div>
            <select value={scopeMode} onChange={(e) => setScopeMode(e.target.value)} style={S.inputSmall}>
              <option value="cuerpo">Cuerpo</option>
              <option value="compania">Compañía</option>
            </select>

            {scopeMode === "compania" && (
              <select value={scopeCompaniaId} onChange={(e) => setScopeCompaniaId(e.target.value)} style={S.inputSmall}>
                <option value="">— Seleccionar compañía —</option>
                {companiasCat.map((c) => (
                  <option key={c.id} value={c.id}>{c.nombre}</option>
                ))}
              </select>
            )}

            {!scopeReady && (
              <div style={{ color: "#b45309", fontWeight: 900 }}>Selecciona una compañía para generar.</div>
            )}
          </div>

          <div style={{ fontWeight: 1000 }}>Resultados: {resultados}</div>
        </div>

        <div style={S.actionsRow}>
          <button style={S.btnGhost} onClick={limpiarFiltros}>Limpiar filtros</button>
          <button
            style={{ ...S.btn, ...(resultados === 0 || !scopeReady ? S.btnDisabled : {}) }}
            onClick={generarInformePDF}
            disabled={resultados === 0 || !scopeReady}
          >
            Generar Informe PDF
          </button>
          <button
            style={{ ...S.btn, ...(resultados === 0 || !scopeReady ? S.btnDisabled : {}) }}
            onClick={generarInformeAsistenciaPDF}
            disabled={resultados === 0 || !scopeReady}
          >
            Informe Asistencia PDF
          </button>
          <button
            style={{ ...S.btn, ...(resultados === 0 || !scopeReady ? S.btnDisabled : {}) }}
            onClick={generarGraficosPDF}
            disabled={resultados === 0 || !scopeReady}
          >
            Gráficos PDF
          </button>
        </div>
      </div>

      {/* Lista */}
      <div style={S.card}>
        <div style={{ fontWeight: 1000, marginBottom: 10 }}>Partes creados</div>
        {loadError ? (
          <div style={{ ...S.notice, borderColor: "#fca5a5", background: "#fef2f2", color: "#991b1b" }}>
            <div style={{ fontWeight: 900, marginBottom: 4 }}>No se pudo cargar el listado (diagnóstico)</div>
            <div><b>Etapa:</b> {loadError.stage}</div>
            <div><b>Código:</b> {loadError.code || "—"}</div>
            <div style={{ whiteSpace: "pre-wrap" }}><b>Detalle:</b> {loadError.message}</div>
          </div>
        ) : null}


        {resultados === 0 ? (
          <div style={{ ...S.notice, borderColor: "#fdba74", background: "#fff7ed", color: "#7c2d12" }}>
            No hay partes para {anio}.
          </div>
        ) : (
          <div style={{ display: "grid", gap: 10 }}>
            {partesFiltrados.map((p) => {
              const ymd = p?.fechaEmergencia || ymdFromAny(p?.createdAt);
              const tipoTxt = p?.tipoEmergenciaNombre || p?.tipo_emergencia_nombre || "—";
              const subTxt = p?.subTipoEmergenciaNombre || p?.sub_tipo_emergencia_nombre || "";
              const comps = (p?.companias_despachadas_nombres || []).join(", ");
              const asistentesN = countAsistentesForScope(p, scope);

              return (
                <div key={p.id} style={S.row}>
                  <div style={S.rowLeft}>
                    <div style={S.rowTitle}>
                      <span style={{ fontWeight: 1000 }}>{p?.correlativo || "—"}</span>
                      <span style={S.badge}>{ymd || "—"} {p?.horaDespacho ? p.horaDespacho : ""}</span>
                    </div>
                    <div style={S.rowSub}>
                      <b>{tipoTxt}</b>{subTxt ? ` · ${subTxt}` : ""} · {p?.direccion || "—"} · {p?.comuna || "—"}
                    </div>
                    {comps && <div style={{ ...S.rowSub, color: "#475569" }}><b>Compañías:</b> {comps}</div>}
                    <div style={{ ...S.rowSub, color: "#475569" }}><b>Asistentes:</b> {asistentesN}</div>
                  </div>

                  <div style={S.rowActions}>
                    <button style={S.btnSmall} onClick={() => generarPDFParte(p)}>PDF Parte</button>
                    {canEdit(perfil) && (
                      <button style={S.btnSmall} onClick={() => navigate(`${partesBase}/editar?id=${p.id}`)}>Editar</button>
                    )}
                    {canDelete(perfil) && (
                      <button style={S.btnDanger} onClick={() => eliminarParte(p.id)}>Eliminar</button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

    </div>
  );
}

const S = {
  embedWrap: { width: "100%", boxSizing: "border-box" },
  page: { minHeight: "100vh", background: "#0b1220", padding: 20 },
  container: { width: "min(1100px, 96vw)", margin: "0 auto" },

  top: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" },
  h1: { color: "white", fontSize: 44, fontWeight: 1000, letterSpacing: -1 },
  muted: { color: "rgba(255,255,255,.72)", marginTop: 4 },

  card: {
    marginTop: 16,
    background: "white",
    borderRadius: 22,
    padding: 18,
    boxShadow: "0 20px 80px rgba(0,0,0,.35)",
    boxSizing: "border-box",
    overflow: "hidden",
  },

  filtersGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
    gap: 14,
    alignItems: "start",
  },

  field: { minWidth: 0 },
  label: { fontSize: 12, fontWeight: 1000, color: "#0b1220" },
  hint: { fontSize: 12, color: "#64748b", marginTop: 6 },
  input: {
    width: "100%",
    padding: 12,
    borderRadius: 12,
    border: "1px solid #e5e7eb",
    outline: "none",
    boxSizing: "border-box",
    minWidth: 0,
  },
  inputSmall: {
    padding: "10px 12px",
    borderRadius: 12,
    border: "1px solid #e5e7eb",
    outline: "none",
    boxSizing: "border-box",
    minWidth: 220,
  },

  scopeRow: {
    display: "flex",
    justifyContent: "space-between",
    gap: 12,
    flexWrap: "wrap",
    alignItems: "center",
    marginTop: 14,
    paddingTop: 14,
    borderTop: "1px solid #e5e7eb",
  },

  actionsRow: { display: "flex", gap: 10, flexWrap: "wrap", marginTop: 14 },

  btn: {
    padding: "10px 14px",
    borderRadius: 12,
    border: "1px solid #0b1220",
    background: "#0b1220",
    color: "white",
    fontWeight: 900,
    cursor: "pointer",
  },
  btnDisabled: { opacity: 0.45, cursor: "not-allowed" },
  btnGhost: {
    padding: "10px 14px",
    borderRadius: 12,
    border: "1px solid #e5e7eb",
    background: "white",
    color: "#0b1220",
    fontWeight: 900,
    cursor: "pointer",
  },
  btnDanger: {
    padding: "10px 14px",
    borderRadius: 12,
    border: "1px solid #ef4444",
    background: "#ef4444",
    color: "white",
    fontWeight: 900,
    cursor: "pointer",
  },
  btnSmall: {
    padding: "9px 12px",
    borderRadius: 12,
    border: "1px solid #e5e7eb",
    background: "white",
    color: "#0b1220",
    fontWeight: 900,
    cursor: "pointer",
  },

  notice: {
    padding: 14,
    borderRadius: 14,
    border: "1px solid #e5e7eb",
    background: "#f8fafc",
    color: "#0b1220",
    fontWeight: 900,
  },

  row: {
    display: "flex",
    gap: 12,
    alignItems: "flex-start",
    justifyContent: "space-between",
    border: "1px solid #e5e7eb",
    borderRadius: 16,
    padding: 14,
    flexWrap: "wrap",
  },
  rowLeft: { minWidth: 260, flex: 1 },
  rowTitle: { display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" },
  badge: {
    fontSize: 12,
    color: "#0b1220",
    background: "#f1f5f9",
    border: "1px solid #e2e8f0",
    padding: "6px 10px",
    borderRadius: 999,
    fontWeight: 900,
  },
  rowSub: { marginTop: 6, color: "#0b1220" },
  rowActions: { display: "flex", gap: 8, flexWrap: "wrap" },
};